import { distributionPercentages } from '@src/packages/dashboard/components/ReportSummary/utils/distribution';
import { useMemo } from 'react';

import { useIntl } from 'react-intl';

import {
  COMPLIANCE_RANGES,
  type DistributionItem,
  type ProgressDistributionItem,
} from '@src/packages/dashboard/components/ReportSummary';

import type {
  ComplianceRangeKey,
  OutcomesSectionData,
  UsersSectionData,
} from '@src/models/interfaces/executionsHistoryDashboard.interface';

import { RESULT_COLORS } from '../constants';
import { getPeriodResult } from '../historySelectors';
import type { OpenTaskDrilldown } from '../types';

/** Period result donut, task compliance ranges and user compliance distribution. */
export const useTasksDistributions = (
  outcomes: OutcomesSectionData | undefined,
  users: UsersSectionData | undefined,
  openDrilldown: OpenTaskDrilldown,
) => {
  const intl = useIntl();

  return useMemo(() => {
    const results = outcomes ? getPeriodResult(outcomes.result) : [];
    const ordersInPeriod = results.reduce((total, result) => total + result.value, 0);
    const shares = distributionPercentages(results.map(result => result.value));
    const periodResult: DistributionItem[] = results.map((result, index) => ({
      key: result.key,
      label: intl.formatMessage({ id: `dashboards.tasks.summary.result.${result.key}` }),
      note: intl.formatMessage({ id: `dashboards.tasks.summary.result.${result.key}.detail` }),
      value: result.value,
      percentage: shares[index],
      color: RESULT_COLORS[result.key],
    }));

    const taskCompliance: ProgressDistributionItem[] = COMPLIANCE_RANGES.map(range => {
      const count = outcomes?.taskRanges[range.key as ComplianceRangeKey] ?? 0;
      return {
        key: range.key,
        label: range.label,
        count,
        color: range.color,
        actionLabel: intl.formatMessage(
          { id: 'dashboards.tasks.summary.taskCompliance.openRange' },
          { count, range: range.label },
        ),
        onClick: () => openDrilldown({
          kind: 'taskRange',
          range,
          title: intl.formatMessage(
            { id: 'dashboards.tasks.summary.taskCompliance.drawerTitle' },
            { range: range.label },
          ),
        }),
      };
    });

    const userDistribution: ProgressDistributionItem[] = COMPLIANCE_RANGES.map((range, index) => {
      const count = users?.userRanges[range.key as ComplianceRangeKey] ?? 0;
      const label = intl.formatMessage({ id: `dashboards.tasks.summary.userRange.${index}` });
      return {
        key: range.key,
        label,
        count,
        color: range.color,
        hint: intl.formatMessage({ id: 'dashboards.tasks.summary.drilldown.available' }, { label }),
        actionLabel: intl.formatMessage(
          { id: 'dashboards.tasks.summary.userDistribution.openRange' },
          { count, range: label },
        ),
        onClick: () => openDrilldown({
          kind: 'userRange',
          range,
          label,
          title: intl.formatMessage(
            { id: 'dashboards.tasks.summary.userDistribution.usersDrawerTitle' },
            { range: label },
          ),
        }),
      };
    });

    return {
      periodResult: {
        items: periodResult,
        centerContent: String(ordersInPeriod),
        ariaLabel: intl.formatMessage({ id: 'dashboards.tasks.summary.periodResult' }),
      },
      taskCompliance,
      userDistribution,
    };
  }, [intl, openDrilldown, outcomes, users]);
};
